"use client";

import React, { ReactNode } from 'react';
import { useUseCelo } from '../providers/UseCeloProvider';
import { usePricing } from '../hooks/usePricing';

/**
 * Base props for feature components
 */
interface BaseFeatureProps {
  children: ReactNode;
  fallback?: ReactNode;
  loading?: ReactNode;
}

/**
 * Props for single feature component
 */
export interface FeatureProps extends BaseFeatureProps {
  name: string;
}

/**
 * Declarative component for feature gating
 * Renders children only if user has access to the specified feature
 */
export function Feature({
  name,
  children,
  fallback = null,
  loading = null
}: FeatureProps) {
  const { hasFeature, loading: contextLoading } = useUseCelo();

  // Show loading state
  if (contextLoading) {
    return <>{loading}</>;
  }

  // Render children if has access, fallback otherwise
  return <>{hasFeature(name) ? children : fallback}</>;
}

/**
 * Component for checking multiple features
 */
interface MultiFeaturesProps extends BaseFeatureProps {
  features: string[];
  requireAll?: boolean;
  renderPartial?: (availableFeatures: string[]) => ReactNode;
}

export function MultiFeatures({
  features,
  children,
  fallback = null,
  loading = null,
  requireAll = true,
  renderPartial
}: MultiFeaturesProps) {
  const { hasFeature, loading: contextLoading } = useUseCelo();

  // Show loading state
  if (contextLoading) {
    return <>{loading}</>;
  }

  const availableFeatures = features.filter(feature => hasFeature(feature));

  // Check if conditions are met
  const hasRequiredAccess = requireAll
    ? features.every(feature => hasFeature(feature))
    : availableFeatures.length > 0;

  // If partial rendering is enabled and we have some features
  if (renderPartial && availableFeatures.length > 0 && (!requireAll || !hasRequiredAccess)) {
    return <>{renderPartial(availableFeatures)}</>;
  }

  // Render children if has access, fallback otherwise
  return <>{hasRequiredAccess ? children : fallback}</>;
}

/**
 * Higher-order component for feature gating
 */
export function withFeatureGate<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  featureName: string,
  fallback?: ReactNode
) {
  const FeatureGatedComponent = (props: P) => (
    <Feature name={featureName} fallback={fallback}>
      <WrappedComponent {...props} />
    </Feature>
  );

  FeatureGatedComponent.displayName = `withFeatureGate(${WrappedComponent.displayName || WrappedComponent.name})`;

  return FeatureGatedComponent;
}

/**
 * Component that shows content only during trial period
 */
interface TrialOnlyProps extends BaseFeatureProps {
  showDaysRemaining?: boolean;
}

export function TrialOnly({
  children,
  fallback = null,
  loading = null,
  showDaysRemaining = false
}: TrialOnlyProps) {
  const { isTrialing, loading: contextLoading } = usePricing();

  // Show loading state
  if (contextLoading) {
    return <>{loading}</>;
  }

  if (!isTrialing) {
    return <>{fallback}</>;
  }

  return (
    <>
      {children}
      {showDaysRemaining && (
        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
          Trial period active
        </div>
      )}
    </>
  );
}

/**
 * Component that shows upgrade prompts based on feature access
 */
interface UpgradePromptProps {
  feature: string;
  title?: string;
  description?: string;
  buttonText?: string;
  onUpgrade?: () => void;
  className?: string;
}

export function UpgradePrompt({
  feature,
  title = 'Upgrade Required',
  description,
  buttonText = 'Upgrade Now',
  onUpgrade,
  className = ''
}: UpgradePromptProps) {
  const { upgradeUrl } = usePricing();
  const defaultDescription = `Access to ${feature.replace(/[_-]/g, ' ')} requires an upgraded plan.`;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Default upgrade behavior - redirect to upgrade URL
      window.open(upgradeUrl, '_blank');
    }
  };

  return (
    <div className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 ${className}`}>
      <div className="text-gray-900 font-medium mb-2">{title}</div>
      <div className="text-gray-600 text-sm mb-4">
        {description || defaultDescription}
      </div>
      <button
        onClick={handleUpgrade}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        {buttonText}
      </button>
    </div>
  );
}

/**
 * Component that renders different content based on plan tier
 */
interface PlanTierProps extends BaseFeatureProps {
  plans: {
    [planName: string]: ReactNode;
  };
}

export function PlanTier({
  children,
  plans,
  fallback = null,
  loading = null
}: PlanTierProps) {
  const { userPlan, loading: contextLoading } = useUseCelo();

  // Show loading state
  if (contextLoading) {
    return <>{loading}</>;
  }

  const currentPlanName = userPlan?.name;

  if (currentPlanName && plans[currentPlanName]) {
    return <>{plans[currentPlanName]}</>;
  }

  if (currentPlanName) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Component that shows content based on subscription status
 */
interface SubscriptionGateProps extends BaseFeatureProps {
  requireActive?: boolean;
  requirePaid?: boolean;
  showForTrial?: boolean;
}

export function SubscriptionGate({
  children,
  fallback = null,
  loading = null,
  requireActive = true,
  requirePaid = false,
  showForTrial = true
}: SubscriptionGateProps) {
  const { isSubscribed, isTrialing, loading: contextLoading } = usePricing();

  // Show loading state
  if (contextLoading) {
    return <>{loading}</>;
  }

  // Check conditions
  const hasValidSubscription = isSubscribed && (showForTrial || !isTrialing);
  const meetsRequirements = requireActive ? hasValidSubscription : true;

  if (requirePaid && isTrialing) {
    return <>{fallback}</>;
  }

  return <>{meetsRequirements ? children : fallback}</>;
}